import React, { useEffect, useState, useCallback, useMemo } from "react";
import styled from "styled-components";
import { useParams, useNavigate } from "react-router-dom";
import GeneralSettings from "./GeneralSettings";
import IntegrationSettings from "./IntegrationSettings";
import EmailTemplateSettings from "./EmailTemplateSettings";
import DeadlineDefaultSettings from "./DeadlineDefaultSettings";
import EditWindowSettings from "./EditWindowSettings";
import { SettingsContainer } from "./styles/shared";
import dayjs from "dayjs";
import {
    getCourseSettings,
    updateCourseSettings,
    listCourseEmailTemplates,
    createCourseEmailTemplate,
    updateCourseEmailTemplate,
    deleteCourseEmailTemplate,
    listEmailTemplates,
    copyGlobalTemplateToCourse,
    uploadCourseTemplateToGlobal,
    fetchGroupsByCourseId,
} from "../../../admin";
import { getTeamAllSessions, setReviewDeadlineForAll } from "../../../firebase";
import { HomeHeader } from "../../Home/HomeHeader";
import BackgroundBlock from "../../Home/BackgroundBlock";
import { useAuth } from "../../../contexts/AuthContext";
import { FiMail, FiDownload, FiChevronLeft, FiStar, FiX, FiLayers, FiChevronRight, FiSearch } from "react-icons/fi";
import { toast } from "sonner";
import ConfirmDialog from "../../../components/common/ConfirmDialog";

const CourseSettings = () => {
    const { courseId } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [course, setCourse] = useState(null);
    const [artifacts, setArtifacts] = useState({
        discordEnabled: true,
        googleDocsEnabled: true,
        pullRequestsEnabled: true,
        figmaEnabled: true,
        fileEnabled: true,
    });
    const [savedArtifacts, setSavedArtifacts] = useState(null);
    const [defaultTemplateId, setDefaultTemplateId] = useState(null);
    const [courseTemplates, setCourseTemplates] = useState([]);
    const [globalTemplates, setGlobalTemplates] = useState([]);
    const [savingArtifacts, setSavingArtifacts] = useState(false);
    const [savingDefaultTemplate, setSavingDefaultTemplate] = useState(false);

    // Add template UI state
    const [showPullModal, setShowPullModal] = useState(false);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [editingTemplateId, setEditingTemplateId] = useState(null);
    const [editForm, setEditForm] = useState({ title: "", subject: "", body: "" });
    const [activeTab, setActiveTab] = useState("general");
    const [generalDirty, setGeneralDirty] = useState(false);
    const [shareToPublicTemplateId, setShareToPublicTemplateId] = useState(null);
    const [shareToPublicForm, setShareToPublicForm] = useState({ title: "", subject: "", body: "", description: "" });
    const [shareToPublicSaving, setShareToPublicSaving] = useState(false);
    const [creatingTemplate, setCreatingTemplate] = useState(false);
    const [previewTemplate, setPreviewTemplate] = useState(null);
    const [pendingDeleteTemplateId, setPendingDeleteTemplateId] = useState(null);
    const [pendingTabSwitch, setPendingTabSwitch] = useState(null); // { tabId, type: "general" | "deadlines" }
    const PAGE_SIZE = 5;

    // Deadline tab state
    const [courseSessions, setCourseSessions] = useState([]);
    const [loadingCourseSessions, setLoadingCourseSessions] = useState(false);
    const [deadlineDays, setDeadlineDays] = useState(7);
    const [savedDeadlineDays, setSavedDeadlineDays] = useState(7);
    const [savingDeadlineDays, setSavingDeadlineDays] = useState(false);
    const [deadlineEnabled, setDeadlineEnabled] = useState(false);
    const [savedDeadlineEnabled, setSavedDeadlineEnabled] = useState(false);
    const [savingDeadlineEnabled, setSavingDeadlineEnabled] = useState(false);

    // Edit settings state
    const [editEnabled, setEditEnabled] = useState(false);
    const [savedEditEnabled, setSavedEditEnabled] = useState(false);
    const [savingEditEnabled, setSavingEditEnabled] = useState(false);
    const [editWindowDays, setEditWindowDays] = useState(7);
    const [savedEditWindowDays, setSavedEditWindowDays] = useState(7);
    const [savingEditSettings, setSavingEditSettings] = useState(false);

    const [currentPage, setCurrentPage] = useState(1);
    const [librarySearchQuery, setLibrarySearchQuery] = useState("");
    const [librarySortBy, setLibrarySortBy] = useState("newest"); // newest | oldest | title-asc | title-desc

    const filteredLibraryTemplates = librarySearchQuery.trim() === ""
        ? globalTemplates
        : globalTemplates.filter((t) => {
            const q = librarySearchQuery.trim().toLowerCase();
            return (t.title || "").toLowerCase().includes(q) ||
                (t.description || "").toLowerCase().includes(q) ||
                (t.subject || "").toLowerCase().includes(q) ||
                (t.body || "").toLowerCase().includes(q);
        });
    const sortedLibraryTemplates = [...filteredLibraryTemplates].sort((a, b) => {
        if (librarySortBy === "newest") {
            const da = a.createdAt ? new Date(a.createdAt).getTime() : 0;
            const db = b.createdAt ? new Date(b.createdAt).getTime() : 0;
            return db - da;
        }
        if (librarySortBy === "oldest") {
            const da = a.createdAt ? new Date(a.createdAt).getTime() : 0;
            const db = b.createdAt ? new Date(b.createdAt).getTime() : 0;
            return da - db;
        }
        if (librarySortBy === "title-asc") {
            return (a.title || "").localeCompare(b.title || "", undefined, { sensitivity: "base" });
        }
        if (librarySortBy === "title-desc") {
            return (b.title || "").localeCompare(a.title || "", undefined, { sensitivity: "base" });
        }
        return 0;
    });
    const totalTemplates = sortedLibraryTemplates.length;
    const totalPages = Math.ceil(totalTemplates / PAGE_SIZE);
    const paginatedTemplates = sortedLibraryTemplates.slice(
        (currentPage - 1) * PAGE_SIZE,
        currentPage * PAGE_SIZE
    );

    useEffect(() => {
        setCurrentPage(1);
    }, [librarySearchQuery, librarySortBy]);

    const loadCourseSettings = useCallback(async () => {
        if (!courseId) {
            setError("Missing course id");
            setLoading(false);
            return;
        }
        try {
            const settings = await getCourseSettings(courseId);
            if (!settings) {
                setError("Course not found");
                setLoading(false);
                return;
            }
            setCourse(settings);
            const base = settings.settings?.artifacts ?? {
                discordEnabled: true,
                googleDocsEnabled: true,
                pullRequestsEnabled: true,
                figmaEnabled: true,
                fileEnabled: true,
            };
            
            setArtifacts(base);
            setSavedArtifacts(base);
            setDefaultTemplateId(settings.defaultTemplateId ?? null);
            const dd = settings.settings?.deadlineDays ?? 7;
            setDeadlineDays(dd);
            setSavedDeadlineDays(dd);
            const de = settings.settings?.deadlineEnabled ?? true;
            setDeadlineEnabled(de);
            setSavedDeadlineEnabled(de);
            const ee = settings.settings?.editEnabled ?? true;
            setEditEnabled(ee);
            setSavedEditEnabled(ee);
            const ew = settings.settings?.editWindowDays ?? 7;
            setEditWindowDays(ew);
            setSavedEditWindowDays(ew);
            try {
                const templates = await listCourseEmailTemplates(courseId);
                setCourseTemplates(templates);
            } catch (templateErr) {
                console.error("Error loading course email templates:", templateErr);
                setCourseTemplates([]);
            }
            try {
                const global = await listEmailTemplates();
                setGlobalTemplates(global);
            } catch (globalErr) {
                console.error("Error loading global email templates:", globalErr);
                setGlobalTemplates([]);
            }
        } catch (err) {
            console.error(err);
            setError(err.message || "Failed to load course settings");
        } finally {
            setLoading(false);
        }
    }, [courseId]);

    const artifactDirty = useMemo(() => {
        if (!savedArtifacts) return false;
    
        return Object.keys({
            ...savedArtifacts,
            ...artifacts,
        }).some((key) => artifacts[key] !== savedArtifacts[key]);
    
    }, [artifacts, savedArtifacts]);

    useEffect(() => {
        setLoading(true);
        loadCourseSettings();
    }, [loadCourseSettings]);

    /** Refresh only course basic info and artifacts (no full page reload). */
    const refreshCourseOnly = useCallback(async () => {
        if (!courseId) return null;
        const settings = await getCourseSettings(courseId);
        if (settings) {
            setCourse(settings);
            const base =
                settings.settings?.artifacts ?? {
                    discordEnabled: true,
                    googleDocsEnabled: true,
                    pullRequestsEnabled: true,
                    figmaEnabled: true,
                    fileEnabled: true,
                };
            setArtifacts(base);
            setSavedArtifacts(base);
            return settings;
        }
        return null;
    }, [courseId]);

    const handleArtifactChange = (key, value) => {
        setArtifacts((prev) => ({ ...prev, [key]: value }));
    };

    const handleDiscardArtifacts = () => {
        if (!course) return;
        const base =
            course.settings?.artifacts ??
            {
                discordEnabled: true,
                googleDocsEnabled: true,
                pullRequestsEnabled: true,
                figmaEnabled: true,
                fileEnabled: true,
            };
        setArtifacts(base);
        setSavedArtifacts(base);
    };

    const handleSaveArtifacts = async () => {
        setSavingArtifacts(true);
        try {
            await updateCourseSettings(courseId, {
                settings: { artifacts },
            });
            setSavedArtifacts({ ...artifacts });
            toast.success("Integration settings saved.");
        } catch (err) {
            console.error(err);
            toast.error("Failed to save integration settings.");
        } finally {
            setSavingArtifacts(false);
        }
    };

    const handleDefaultTemplateChange = async (e) => {
        const id = e.target.value || null;
        setDefaultTemplateId(id);
        setSavingDefaultTemplate(true);
        try {
            await updateCourseSettings(courseId, { defaultTemplateId: id });
        } catch (err) {
            console.error(err);
            setDefaultTemplateId(defaultTemplateId);
            alert("Failed to set default template.");
        } finally {
            setSavingDefaultTemplate(false);
        }
    };

    const refreshCourseTemplates = async () => {
        const list = await listCourseEmailTemplates(courseId);
        setCourseTemplates(list);
    };

    const handlePullFromPublic = async (globalTemplateId) => {
        if (!user?.email) return;
        try {
            await copyGlobalTemplateToCourse(courseId, globalTemplateId, user.email);
            await refreshCourseTemplates();
            setShowPullModal(false);
            toast.success("Template imported successfully.");
        } catch (err) {
            console.error(err);
            toast.error(err.message || "Failed to import template.");
        }
    };

    const handleCreateNew = async () => {
        if (!editForm.title?.trim() || !user?.email) return;
        setCreatingTemplate(true);
        try {
            await createCourseEmailTemplate(courseId, {
                title: editForm.title,
                subject: editForm.subject,
                body: editForm.body,
                sourceTemplateId: null,
                updateBy: user.email,
            });
            await refreshCourseTemplates();
            setShowCreateModal(false);
            setEditForm({ title: "", subject: "", body: "" });
            toast.success("Template created successfully.");
        } catch (err) {
            console.error(err);
            toast.error("Failed to create template.");
        } finally {
            setCreatingTemplate(false);
        }
    };

    const handleSaveEdit = async () => {
        if (!editingTemplateId || !user?.email) return;
        try {
            await updateCourseEmailTemplate(courseId, editingTemplateId, {
                title: editForm.title,
                subject: editForm.subject,
                body: editForm.body,
                updateBy: user.email,
            });
            await refreshCourseTemplates();
            setEditingTemplateId(null);
            setEditForm({ title: "", subject: "", body: "" });
            toast.success("Template saved successfully.");
        } catch (err) {
            console.error(err);
            toast.error("Failed to update template.");
        }
    };

    const handleDeleteTemplate = (templateId) => {
        setPendingDeleteTemplateId(templateId);
    };

    const confirmDeleteTemplate = async () => {
        const templateId = pendingDeleteTemplateId;
        setPendingDeleteTemplateId(null);
        try {
            await deleteCourseEmailTemplate(courseId, templateId);
            if (defaultTemplateId === templateId) {
                setDefaultTemplateId(null);
                await updateCourseSettings(courseId, { defaultTemplateId: null });
            }
            await refreshCourseTemplates();
        } catch (err) {
            console.error(err);
            toast.error("Failed to delete template.");
        }
    };

    const handleSetAsDefault = (templateId) => {
        setDefaultTemplateId(templateId);
        updateCourseSettings(courseId, { defaultTemplateId: templateId })
            .then(() => toast.success("Default template updated."))
            .catch((err) => {
                console.error(err);
                toast.error("Failed to set default template.");
            });
    };

    const handleOpenShareToPublic = (templateId) => {
        const t = courseTemplates.find((c) => c.id === templateId);
        setShareToPublicTemplateId(templateId);
        setShareToPublicForm({
            title: t?.title ?? "",
            subject: t?.subject ?? "",
            body: t?.body ?? "",
            description: "",
        });
    };

    const handleSubmitShareToPublic = async () => {
        if (!user?.email || !shareToPublicTemplateId) return;
        const desc = shareToPublicForm.description.trim();
        if (!desc) {
            toast.error("Description is required when sharing to public templates.");
            return;
        }
        if (!shareToPublicForm.title?.trim()) {
            toast.error("Template name is required.");
            return;
        }
        setShareToPublicSaving(true);
        try {
            await uploadCourseTemplateToGlobal(courseId, shareToPublicTemplateId, user.email, desc, {
                title: shareToPublicForm.title.trim(),
                subject: shareToPublicForm.subject ?? "",
                body: shareToPublicForm.body ?? "",
            });
            const global = await listEmailTemplates();
            setGlobalTemplates(global);
            setShareToPublicTemplateId(null);
            setShareToPublicForm({ title: "", subject: "", body: "", description: "" });
            toast.success("Template published to the community library.");
        } catch (err) {
            console.error(err);
            toast.error(err.message || "Failed to share template.");
        } finally {
            setShareToPublicSaving(false);
        }
    };

    const openEdit = (t) => {
        setEditingTemplateId(t.id);
        setEditForm({ title: t.title ?? "", subject: t.subject ?? "", body: t.body ?? "" });
    };

    useEffect(() => {
        if (activeTab !== "deadlines" || !courseId) return;
        setLoadingCourseSessions(true);
        fetchGroupsByCourseId(courseId)
            .then((groups) =>
                Promise.all(groups.map((g) => getTeamAllSessions(g.id)))
            )
            .then((results) => setCourseSessions(results.flat()))
            .catch((err) => console.error("Error loading sessions for deadlines:", err))
            .finally(() => setLoadingCourseSessions(false));
    }, [activeTab, courseId]);

    // Turning ON just updates local state 
    // Turning OFF auto-saves immediately
    const handleDeadlineEnabledChange = async (enabled) => {
        setDeadlineEnabled(enabled);
        if (!enabled) {
            setSavingDeadlineEnabled(true);
            try {
                await updateCourseSettings(courseId, { settings: { deadlineEnabled: false } });
                setSavedDeadlineEnabled(false);
                toast.success("Assessment deadlines disabled.");
            } catch (err) {
                console.error(err);
                setDeadlineEnabled(true);
                toast.error("Failed to disable deadline setting.");
            } finally {
                setSavingDeadlineEnabled(false);
            }
        }
    };

    // Saves both deadlineEnabled (true) and deadlineDays together.
    const handleSaveDeadlineDays = async (days) => {
        setSavingDeadlineDays(true);
        try {
            await updateCourseSettings(courseId, { settings: { deadlineDays: days, deadlineEnabled: deadlineEnabled } });
            setSavedDeadlineDays(days);
            setSavedDeadlineEnabled(deadlineEnabled);
            toast.success("Deadline settings saved.");
        } catch (err) {
            console.error(err);
            toast.error("Failed to save deadline settings.");
        } finally {
            setSavingDeadlineDays(false);
        }
    };

    const handleDiscardDeadlineSettings = () => {
        setDeadlineEnabled(savedDeadlineEnabled);
        setDeadlineDays(savedDeadlineDays);
    };

    const handleEditEnabledChange = async (enabled) => {
        setEditEnabled(enabled);
        if (!enabled) {
            setSavingEditEnabled(true);
            try {
                await updateCourseSettings(courseId, { settings: { editEnabled: false } });
                setSavedEditEnabled(false);
                toast.success("Rating edits disabled.");
            } catch (err) {
                console.error(err);
                setEditEnabled(true);
                toast.error("Failed to update edit settings.");
            } finally {
                setSavingEditEnabled(false);
            }
        }
    };

    const handleSaveEditSettings = async () => {
        setSavingEditSettings(true);
        try {
            await updateCourseSettings(courseId, { settings: { editEnabled: true, editWindowDays } });
            setSavedEditEnabled(true);
            setSavedEditWindowDays(editWindowDays);
            toast.success("Edit settings saved.");
        } catch (err) {
            console.error(err);
            toast.error("Failed to save edit settings.");
        } finally {
            setSavingEditSettings(false);
        }
    };

    const handleDiscardEditSettings = () => {
        setEditEnabled(savedEditEnabled);
        setEditWindowDays(savedEditWindowDays);
    };

    const editSettingsDirty =
        editEnabled !== savedEditEnabled ||
        (editEnabled && editWindowDays !== savedEditWindowDays);

    const deadlineSettingsDirty =
        deadlineEnabled !== savedDeadlineEnabled ||
        (deadlineEnabled && deadlineDays !== savedDeadlineDays);

    useEffect(() => {
        if (!deadlineSettingsDirty && !editSettingsDirty) return;
        const handler = (e) => {
            e.preventDefault();
            e.returnValue = '';
        };
        window.addEventListener('beforeunload', handler);
        return () => window.removeEventListener('beforeunload', handler);
    }, [deadlineSettingsDirty, editSettingsDirty]);

    const handleSetDeadlineForAll = async (sessions, days) => {
        await setReviewDeadlineForAll(sessions, days);
        const groups = await fetchGroupsByCourseId(courseId);
        const results = await Promise.all(groups.map((g) => getTeamAllSessions(g.id)));
        setCourseSessions(results.flat());
        toast.success(`Deadline set for ${sessions.length} activit${sessions.length === 1 ? "y" : "ies"}.`);
    };

    if (loading) return <PageWrapper><HomeHeader text="Course Settings" link="/admin/home" /><Main><p>Loading...</p></Main></PageWrapper>;
    if (error || !course) return <PageWrapper><HomeHeader text="Course Settings" link="/admin/home" /><Main><p>{error || "Course not found"}</p><BackButton onClick={() => navigate("/admin/home")}>Back to Admin Home</BackButton></Main></PageWrapper>;

    const tabs = [
        { id: "general", label: "General" },
        { id: "integrations", label: "Integrations" },
        { id: "emails", label: "Email templates" },
        { id: "deadlines", label: "Work Assessment Settings" },
    ];

    const start = (currentPage - 1) * PAGE_SIZE + 1;
    const end = Math.min(currentPage * PAGE_SIZE, totalTemplates);

    return (
        <>
            <HomeHeader text={`Course Settings`} link="/admin/home" />
            <PageWrapper>
                <BackgroundBlock>
                    <Layout>
                        <TabNav>
                            {tabs.map((tab) => (
                                <TabButton
                                    key={tab.id}
                                    $active={activeTab === tab.id}
                                    onClick={() => {
                                        if (activeTab === "general" && generalDirty && tab.id !== "general") {
                                            setPendingTabSwitch({ tabId: tab.id, type: "general" });
                                            return;
                                        }
                                        if (activeTab === "integrations" && artifactDirty && tab.id !== "integrations") {
                                            setPendingTabSwitch({ tabId: tab.id, type: "integrations" });
                                            return;
                                        }
                                        if (activeTab === "deadlines" && deadlineSettingsDirty && tab.id !== "deadlines") {
                                            setPendingTabSwitch({ tabId: tab.id, type: "deadlines" });
                                            return;
                                        }
                                        if (activeTab === "deadlines" && editSettingsDirty && tab.id !== "deadlines") {
                                            setPendingTabSwitch({ tabId: tab.id, type: "editSettings" });
                                            return;
                                        }
                                        setActiveTab(tab.id);
                                    }}
                                >
                                    {tab.label}
                                </TabButton>
                            ))}
                        </TabNav>
                        <TabPanel>
                            {activeTab === "general" && (
                                <GeneralSettings
                                    courseId={courseId}
                                    course={course}
                                    onCourseUpdate={(updates) => setCourse((prev) => (prev ? { ...prev, ...updates } : null))}
                                    onDirtyChange={setGeneralDirty}
                                    onRefresh={refreshCourseOnly}
                                />
                            )}
                            {activeTab === "integrations" && (
                                <IntegrationSettings
                                    artifacts={artifacts}
                                    onChange={handleArtifactChange}
                                    savingArtifacts={savingArtifacts}
                                    onSave={handleSaveArtifacts}
                                    dirty={artifactDirty}
                                    onDiscard={handleDiscardArtifacts}
                                />
                            )}
                            {activeTab === "emails" && (
                                <EmailTemplateSettings
                                    courseTemplates={courseTemplates}
                                    defaultTemplateId={defaultTemplateId}
                                    setShowCreateModal={setShowCreateModal}
                                    setEditForm={setEditForm}
                                    shareToPublicSaving={shareToPublicSaving}
                                    savingDefaultTemplate={savingDefaultTemplate}
                                    onDefaultTemplateChange={handleDefaultTemplateChange}
                                    onOpenEdit={openEdit}
                                    onSetAsDefault={handleSetAsDefault}
                                    onOpenShareToPublic={handleOpenShareToPublic}
                                    onDeleteTemplate={handleDeleteTemplate}
                                    onPullClick={() => {
                                        setShowPullModal(true);
                                        setLibrarySearchQuery("");
                                        setCurrentPage(1);
                                    }}
                                />
                            )}
                            {activeTab === "deadlines" && (
                                <SettingsContainer>
                                    <DeadlineDefaultSettings
                                        sessions={courseSessions}
                                        loadingSessions={loadingCourseSessions}
                                        onSetDeadline={handleSetDeadlineForAll}
                                        deadlineDays={deadlineDays}
                                        savingDeadlineDays={savingDeadlineDays}
                                        onDeadlineDaysChange={setDeadlineDays}
                                        onSaveDeadlineDays={handleSaveDeadlineDays}
                                        deadlineEnabled={deadlineEnabled}
                                        savedDeadlineEnabled={savedDeadlineEnabled}
                                        onDeadlineEnabledChange={handleDeadlineEnabledChange}
                                        savingDeadlineEnabled={savingDeadlineEnabled}
                                        deadlineSettingsDirty={deadlineSettingsDirty}
                                        onDiscardDeadlineSettings={handleDiscardDeadlineSettings}
                                    />
                                    <EditWindowSettings
                                        deadlineDays={deadlineDays}
                                        deadlineEnabled={deadlineEnabled}
                                        editEnabled={editEnabled}
                                        savedEditEnabled={savedEditEnabled}
                                        onEditEnabledChange={handleEditEnabledChange}
                                        savingEditEnabled={savingEditEnabled}
                                        editWindowDays={editWindowDays}
                                        onEditWindowDaysChange={setEditWindowDays}
                                        onSaveEditSettings={handleSaveEditSettings}
                                        savingEditSettings={savingEditSettings}
                                        editSettingsDirty={editSettingsDirty}
                                        onDiscardEditSettings={handleDiscardEditSettings}
                                    />
                                </SettingsContainer>
                            )}
                        </TabPanel>
                    </Layout>
                </BackgroundBlock>
            </PageWrapper>

            {showPullModal && (
                <ModalOverlay onClick={() => setShowPullModal(false)}>
                    <ModalContent onClick={(e) => e.stopPropagation()}>
                        <LibraryHeader>
                            <LibraryHeaderLeft>
                                <HeaderIconBox>
                                    <FiLayers size={20} />
                                </HeaderIconBox>

                                <div>
                                    <LibraryTitle>Public Templates</LibraryTitle>
                                    <LibrarySubtitle>
                                        Browse and import community-shared email templates
                                    </LibrarySubtitle>
                                </div>
                            </LibraryHeaderLeft>

                            <HeaderCloseButton onClick={() => setShowPullModal(false)}>
                                <FiX size={18} />
                            </HeaderCloseButton>
                        </LibraryHeader>
                        <LibrarySearchWrap>
                            <LibraryFieldGroup>
                                <LibraryLabel>Search</LibraryLabel>
                                <SearchInputWrapper>
                                    <LibrarySearchIcon>
                                        <FiSearch size={16} />
                                    </LibrarySearchIcon>
                                    <LibrarySearchInput
                                        type="text"
                                        placeholder="Search by title, subject, or description..."
                                        value={librarySearchQuery}
                                        onChange={(e) => setLibrarySearchQuery(e.target.value)}
                                    />
                                </SearchInputWrapper>
                            </LibraryFieldGroup>
                            <LibraryFieldGroup>
                                <LibraryLabel htmlFor="library-sort">Sort by</LibraryLabel>
                                <LibraryControls>
                                    <LibrarySortSelect
                                        id="library-sort"
                                        value={librarySortBy}
                                        onChange={(e) => setLibrarySortBy(e.target.value)}
                                    >
                                        <option value="newest">Latest</option>
                                        <option value="oldest">Oldest</option>
                                        <option value="title-asc">Title A–Z</option>
                                        <option value="title-desc">Title Z–A</option>
                                    </LibrarySortSelect>
                                </LibraryControls>
                            </LibraryFieldGroup>
                        </LibrarySearchWrap>
                        <TemplateLibraryList>
                            {globalTemplates.length === 0 ? (
                                <EmptyState>No public templates available.</EmptyState>
                            ) : filteredLibraryTemplates.length === 0 ? (
                                <EmptyState>No templates match your search.</EmptyState>
                            ) : (
                                paginatedTemplates.map((t) => (
                                    <LibraryTemplateCard key={t.id}>
                                        <LibraryTemplateContent>
                                            <LibraryTemplateHeader>
                                                <LibraryTemplateTitle>
                                                    {t.title || "Untitled template"}
                                                </LibraryTemplateTitle>
                                            </LibraryTemplateHeader>

                                            <LibraryTemplateDescription>
                                                {t.description || "Reusable email template from the TeamCAST library."}
                                            </LibraryTemplateDescription>

                                            <LibraryTemplateMeta>
                                                <MetaRow>
                                                    <FiStar size={14} />
                                                    <span>Subject:</span>
                                                    <i>{t.subject || "No subject"}</i>
                                                </MetaRow>

                                                <MetaRow>
                                                    <FiMail size={14} />
                                                    <span>Body:</span> {t.body.length > 50 ? t.body.slice(0, 50) + "..." : t.body}
                                                </MetaRow>
                                            </LibraryTemplateMeta>
                                            <LibraryTemplateFooter>

                                                {t.createdAt && (
                                                    <span>
                                                        Created at {dayjs(t.createdAt).format("MMM D, YYYY")}
                                                    </span>
                                                )}

                                                <LibraryTemplateActions>
                                                    <PreviewButton onClick={() => setPreviewTemplate(t)}>
                                                        <FiMail />
                                                        Preview
                                                    </PreviewButton>

                                                    <ImportButton onClick={() => handlePullFromPublic(t.id)}>
                                                        <FiDownload />
                                                        Import
                                                    </ImportButton>
                                                </LibraryTemplateActions>

                                            </LibraryTemplateFooter>
                                        </LibraryTemplateContent>
                                    </LibraryTemplateCard>
                                ))
                            )}
                        </TemplateLibraryList>
                        <LibraryFooter>
                            <FooterText>
                                {totalTemplates === 0
                                    ? "Showing 0 of 0 templates"
                                    : `Showing ${start}-${end} of ${totalTemplates} templates`}
                            </FooterText>

                            <Pagination>

                                {currentPage > 1 && (
                                    <PrevButton onClick={() => setCurrentPage((p) => p - 1)}>
                                        <FiChevronLeft size={16} />
                                    </PrevButton>
                                )}
                                {Array.from({ length: totalPages }).map((_, i) => {
                                    const page = i + 1;

                                    return (
                                        <PageButton
                                            key={page}
                                            $active={page === currentPage}
                                            onClick={() => setCurrentPage(page)}
                                        >
                                            {page}
                                        </PageButton>
                                    );
                                })}

                                {currentPage < totalPages && (
                                    <NextButton onClick={() => setCurrentPage((p) => p + 1)}>
                                        <FiChevronRight size={16} />
                                    </NextButton>
                                )}
                            </Pagination>
                        </LibraryFooter>
                    </ModalContent>
                </ModalOverlay>
            )}

            {previewTemplate && (
                <ModalOverlay onClick={() => setPreviewTemplate(null)}>
                    <PreviewModal onClick={(e) => e.stopPropagation()}>

                        <PreviewHeader>
                            <div>
                                <label>Subject</label>
                                <h3>{previewTemplate.subject || "Template subject"}</h3>
                            </div>

                            <HeaderCloseButton onClick={() => setPreviewTemplate(null)}>
                                <FiX />
                            </HeaderCloseButton>
                        </PreviewHeader>

                        <PreviewBody>
                            {previewTemplate.body || "No content available."}
                        </PreviewBody>

                        <PreviewFooter>
                            <ImportButton
                                onClick={() => {
                                    handlePullFromPublic(previewTemplate.id);
                                    setPreviewTemplate(null);
                                }}
                            >
                                <FiDownload />
                                Import Template
                            </ImportButton>
                        </PreviewFooter>

                    </PreviewModal>
                </ModalOverlay>
            )}

            {showCreateModal && (
                <EmailTemplateModalOverlay onClick={() => {
                    if (!creatingTemplate) setShowCreateModal(false);
                }}>
                    <EmailTemplateModalBox onClick={(e) => e.stopPropagation()}>
                        <EmailTemplateModalHeader>
                            <div>
                                <h2>Create new template</h2>
                                <p>Customize your message details and dynamic content.</p>
                            </div>
                            <EmailTemplateCloseButton onClick={() => setShowCreateModal(false)} aria-label="Close">
                                <FiX size={24} />
                            </EmailTemplateCloseButton>
                        </EmailTemplateModalHeader>
                        <EmailTemplateModalBody>
                            <EmailTemplateField>
                                <EmailTemplateLabel>Template name <Required>*</Required></EmailTemplateLabel>
                                <EmailTemplateInput
                                    placeholder="Enter template name..."
                                    value={editForm.title}
                                    onChange={(e) => setEditForm((p) => ({ ...p, title: e.target.value }))}
                                />
                            </EmailTemplateField>
                            <EmailTemplateField>
                                <EmailTemplateLabel>Subject line <Required>*</Required></EmailTemplateLabel>
                                <EmailTemplateInput
                                    placeholder="Email subject line..."
                                    value={editForm.subject}
                                    onChange={(e) => setEditForm((p) => ({ ...p, subject: e.target.value }))}
                                />
                            </EmailTemplateField>
                            <EmailTemplateField>
                                <EmailTemplateLabel>Content <Required>*</Required></EmailTemplateLabel>
                                <EmailTemplateTextarea
                                    placeholder="Start writing your template content..."
                                    value={editForm.body}
                                    onChange={(e) => setEditForm((p) => ({ ...p, body: e.target.value }))}
                                    rows={12}
                                />
                                <EmailTemplateContentFooter>
                                    <span>{editForm.body.trim() ? editForm.body.trim().split(/\s+/).length : 0} words</span>
                                </EmailTemplateContentFooter>
                            </EmailTemplateField>
                        </EmailTemplateModalBody>
                        <EmailTemplateModalFooter>
                            <EmailTemplateCancelButton onClick={() => setShowCreateModal(false)}>Cancel</EmailTemplateCancelButton>
                            <EmailTemplateSaveButton onClick={handleCreateNew} disabled={!editForm.title.trim() || !editForm.subject.trim() || !editForm.body.trim()}>
                                {creatingTemplate ? (
                                    <>
                                        <Spinner />
                                        Creating...
                                    </>
                                ) : (
                                    "Create template"
                                )}
                            </EmailTemplateSaveButton>
                        </EmailTemplateModalFooter>
                    </EmailTemplateModalBox>
                </EmailTemplateModalOverlay>
            )}

            {editingTemplateId && (
                <EmailTemplateModalOverlay onClick={() => setEditingTemplateId(null)}>
                    <EmailTemplateModalBox onClick={(e) => e.stopPropagation()}>
                        <EmailTemplateModalHeader>
                            <div>
                                <h2>Edit template</h2>
                                <p>Customize your message details and dynamic content.</p>
                            </div>
                            <EmailTemplateCloseButton onClick={() => setEditingTemplateId(null)} aria-label="Close">
                                <FiX size={24} />
                            </EmailTemplateCloseButton>
                        </EmailTemplateModalHeader>
                        <EmailTemplateModalBody>
                            <EmailTemplateField>
                                <EmailTemplateLabel>Template name <Required>*</Required></EmailTemplateLabel>
                                <EmailTemplateInput
                                    placeholder="Enter template name..."
                                    value={editForm.title}
                                    onChange={(e) => setEditForm((p) => ({ ...p, title: e.target.value }))}
                                />
                            </EmailTemplateField>
                            <EmailTemplateField>
                                <EmailTemplateLabel>Subject line <Required>*</Required></EmailTemplateLabel>
                                <EmailTemplateInput
                                    placeholder="Email subject line..."
                                    value={editForm.subject}
                                    onChange={(e) => setEditForm((p) => ({ ...p, subject: e.target.value }))}
                                />
                            </EmailTemplateField>
                            <EmailTemplateField>
                                <EmailTemplateLabel>Content <Required>*</Required></EmailTemplateLabel>
                                <EmailTemplateTextarea
                                    placeholder="Start writing your template content..."
                                    value={editForm.body}
                                    onChange={(e) => setEditForm((p) => ({ ...p, body: e.target.value }))}
                                    rows={12}
                                />
                                <EmailTemplateContentFooter>
                                    <span>{editForm.body.trim() ? editForm.body.trim().split(/\s+/).length : 0} words</span>
                                </EmailTemplateContentFooter>
                            </EmailTemplateField>
                        </EmailTemplateModalBody>
                        <EmailTemplateModalFooter>
                            <EmailTemplateCancelButton onClick={() => setEditingTemplateId(null)}>Cancel</EmailTemplateCancelButton>
                            <EmailTemplateSaveButton onClick={handleSaveEdit} disabled={!editForm.title.trim() || !editForm.subject.trim() || !editForm.body.trim()}>Save changes</EmailTemplateSaveButton>
                        </EmailTemplateModalFooter>
                    </EmailTemplateModalBox>
                </EmailTemplateModalOverlay>
            )}

            {shareToPublicTemplateId && (
                <ModalOverlay onClick={() => {
                    if (!shareToPublicSaving) setShareToPublicTemplateId(null);
                }}>
                    <ShareModalContainer onClick={(e) => e.stopPropagation()}>

                        <ShareModalHeader>
                            <div>
                                <ShareModalTitle>Share to Public Library</ShareModalTitle>
                                <ShareModalSubtitle>
                                    Contribute your template to the TeamCAST community
                                </ShareModalSubtitle>
                            </div>

                            <CloseButton onClick={() => setShareToPublicTemplateId(null)}>
                                <FiX size={20} />
                            </CloseButton>
                        </ShareModalHeader>

                        <ShareModalBody>

                            <FieldGroup>
                                <FieldLabel>Template name <Required>*</Required></FieldLabel>
                                <ShareModalInput
                                    placeholder="Enter template name..."
                                    value={shareToPublicForm.title}
                                    onChange={(e) => setShareToPublicForm((p) => ({ ...p, title: e.target.value }))}
                                />
                            </FieldGroup>
                            <FieldGroup>
                                <FieldLabel>Subject line <Required>*</Required></FieldLabel>
                                <ShareModalInput
                                    placeholder="Email subject line..."
                                    value={shareToPublicForm.subject}
                                    onChange={(e) => setShareToPublicForm((p) => ({ ...p, subject: e.target.value }))}
                                />
                            </FieldGroup>
                            <FieldGroup>
                                <FieldLabel>Content <Required>*</Required></FieldLabel>
                                <ShareModalTextarea
                                    placeholder="Template body..."
                                    value={shareToPublicForm.body}
                                    onChange={(e) => setShareToPublicForm((p) => ({ ...p, body: e.target.value }))}
                                    rows={8}
                                />
                            </FieldGroup>
                            <FieldGroup>
                                <FieldLabel>
                                    Description <Required>*</Required>
                                </FieldLabel>

                                <DescriptionTextarea
                                    placeholder="e.g., Weekly check-in reminder for team members"
                                    value={shareToPublicForm.description}
                                    onChange={(e) => setShareToPublicForm((p) => ({ ...p, description: e.target.value }))}
                                />
                            </FieldGroup>

                            <InfoBox>
                                <FiLayers size={18} />
                                <InfoText>
                                    <strong>Note:</strong> Shared templates will be visible to everyone in TeamCAST.
                                </InfoText>
                            </InfoBox>

                        </ShareModalBody>

                        <ShareModalFooter>

                            <CancelButton
                                onClick={() => setShareToPublicTemplateId(null)}
                                disabled={shareToPublicSaving}
                            >
                                Cancel
                            </CancelButton>

                            <ShareButton
                                onClick={handleSubmitShareToPublic}
                                disabled={shareToPublicSaving || !shareToPublicForm.description.trim() || !shareToPublicForm.title.trim() || !shareToPublicForm.subject.trim() || !shareToPublicForm.body.trim()}
                            >
                                {shareToPublicSaving ? (
                                    <>
                                        <Spinner />
                                        Sharing...
                                    </>
                                ) : (
                                    "Share Template"
                                )}
                            </ShareButton>

                        </ShareModalFooter>

                    </ShareModalContainer>
                </ModalOverlay>
            )}
            {pendingTabSwitch && (
                <ConfirmDialog
                    title="Unsaved Changes"
                    message={
                        pendingTabSwitch.type === "deadlines"
                            ? "You have unsaved deadline settings. Leave without saving?"
                            : pendingTabSwitch.type === "editSettings"
                            ? "You have unsaved edit settings. Leave without saving?"
                            : pendingTabSwitch.type === "integrations"
                            ? "You have unsaved integration changes. Leave without saving?"
                            : "You have unsaved changes. Leave without saving?"
                    }
                    confirmText="Leave"
                    cancelText="Stay"
                    variant="primary"
                    onConfirm={() => {
                        if (pendingTabSwitch.type === "deadlines") handleDiscardDeadlineSettings();
                        if (pendingTabSwitch.type === "editSettings") handleDiscardEditSettings();
                        if (pendingTabSwitch.type === "integrations") handleDiscardArtifacts();
                        setActiveTab(pendingTabSwitch.tabId);
                        setPendingTabSwitch(null);
                    }}
                    onCancel={() => setPendingTabSwitch(null)}
                />
            )}

            {pendingDeleteTemplateId && (
                <ConfirmDialog
                    title="Delete Template?"
                    message="Delete this template? This action cannot be undone."
                    confirmText="Yes, Delete"
                    cancelText="No, Go Back"
                    variant="danger"
                    showIcon
                    footnote="This action cannot be undone."
                    onConfirm={confirmDeleteTemplate}
                    onCancel={() => setPendingDeleteTemplateId(null)}
                />
            )}
        </>
    );
};

const PageWrapper = styled.div`
    background-color: #f8fafc;
    min-height: 100vh;
    display: flex;
    flex-direction: column;
`;

const Layout = styled.div`
    display: flex;
    gap: 24px;
    padding: 16px;
    width: 100%;
    align-items: flex-start;

    @media (max-width: 768px) {
        flex-direction: column;
        gap: 16px;
    }
`;

const TabNav = styled.nav`
    display: flex;
    flex-direction: column;
    min-width: 200px;
    width: 220px;
    background: #fff;
    border-radius: 12px;
    padding: 8px;
    box-shadow: 0 2px 8px rgba(0,0,0,0.06);
    flex-shrink: 0;

    @media (max-width: 768px) {
        width: 100%;
        flex-direction: row;
        flex-wrap: wrap;
        gap: 4px;
    }
`;

const TabButton = styled.button`
    text-align: left;
    padding: 12px 16px;
    border: none;
    border-radius: 8px;
    font-size: 14px;
    font-weight: 500;
    color: ${({ $active }) => ($active ? "#fff" : "#374151")};
    background: ${({ $active }) => ($active ? "#9333ea" : "transparent")};
    cursor: pointer;
    transition: background 0.15s, color 0.15s;
    &:hover {
        background: ${({ $active }) => ($active ? "#7e22ce" : "#f3f4f6")};
        color: ${({ $active }) => ($active ? "#fff" : "#111827")};
    }
`;

const TabPanel = styled.div`
    flex: 1;
    min-width: 0;
`;

const Main = styled.main`
    padding: 20px;
`;

const BackButton = styled.button`
    display: inline-flex;
    align-items: center;
    margin-top: 12px;
    background-color: #fafafc;
    color: #333333;
    border: 3px solid rgba(0, 0, 0, 0.04);
    padding: 8px 14px;
    border-radius: 11px;
    font-family: "SF Pro Text", system-ui, -apple-system, sans-serif;
    font-size: 14px;
    font-weight: 400;
    line-height: 1.29;
    letter-spacing: -0.224px;
    cursor: pointer;
    transition: transform 0.1s ease-in-out;

    &:active {
        transform: scale(0.95);
    }
`;

const ModalOverlay = styled.div`
    position: fixed;
    inset: 0;
    background: rgba(0,0,0,0.4);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 9999;
    padding: 20px;
`;

const ModalContent = styled.div`
    background: #fff;
    border-radius: 12px;
    padding: 24px;
    max-width: 960px;
    width: min(1100px, 95vw);
    max-height: 90vh;
    overflow-y: auto;
    box-shadow: 0 20px 40px rgba(0,0,0,0.2);
    h3 { margin: 0 0 12px 0; }
    p { margin: 0 0 12px 0; color: #6b7280; }
    form input,
    form textarea,
    form select {
    margin-bottom: 12px;
    }
`;


const EmptyState = styled.div`
    grid-column: 1 / -1;

    display:flex;
    flex-direction:column;
    align-items:center;
    justify-content:center;
    padding:60px 20px;

    border:2px dashed #e5e7eb;
    border-radius:16px;
    text-align:center;
    color:#6b7280;

    h3{
        margin-top:12px;
        font-size:18px;
        color:#111827;
        margin-bottom:12px;
    }
`;

/* Email template modal – layout matching Edit Template design */

const EmailTemplateModalOverlay = styled.div`
    position: fixed;
    inset: 0;
    z-index: 9999;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(15, 23, 42, 0.6);
    backdrop-filter: blur(4px);
    padding: 16px;
`;

const EmailTemplateModalBox = styled.div`
    background: #fff;
    width: 100%;
    max-width: 42rem;
    max-height: 90vh;
    border-radius: 0.75rem;
    box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
    display: flex;
    flex-direction: column;
    overflow: hidden;
    border: 1px solid #e2e8f0;
`;

const EmailTemplateModalHeader = styled.div`
    padding: 24px 32px;
    border-bottom: 1px solid #f1f5f9;
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    flex-shrink: 0;
    text-align: left;

    h2 {
        margin: 0;
        font-size: 1.5rem;
        font-weight: 700;
        color: #0f172a;
    }
    p {
        margin: 4px 0 0 0;
        font-size: 0.875rem;
        color: #475569;
    }
`;

const EmailTemplateCloseButton = styled.button`
    background: none;
    border: none;
    color: #94a3b8;
    cursor: pointer;
    padding: 4px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 4px;
    transition: color 0.15s;

    &:hover {
        color: #475569;
    }
`;

const EmailTemplateModalBody = styled.div`
    flex: 1;
    overflow-y: auto;
    padding: 32px;
    display: flex;
    flex-direction: column;
    gap: 24px;
`;

const EmailTemplateField = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
`;

const EmailTemplateLabel = styled.label`
    font-size: 0.875rem;
    font-weight: 600;
    text-align: left;
    color: #334155;
`;

const EmailTemplateInput = styled.input`
    width: 100%;
    padding: 12px 16px;
    border-radius: 0.75rem;
    border: 1px solid #e2e8f0;
    background: #f8fafc;
    font-size: 1rem;
    color: #0f172a;
    transition: box-shadow 0.15s, border-color 0.15s;

    &:focus {
        outline: none;
        border-color: #9333ea;
        box-shadow: 0 0 0 2px rgba(147, 51, 234, 0.2);
    }
    &::placeholder {
        color: #94a3b8;
    }
`;

const EmailTemplateTextarea = styled.textarea`
    width: 100%;
    min-height: 240px;
    padding: 12px 16px;
    border-radius: 0.75rem;
    border: 1px solid #e2e8f0;
    background: #f8fafc;
    font-size: 1rem;
    line-height: 1.625;
    color: #0f172a;
    resize: vertical;
    font-family: inherit;
    transition: box-shadow 0.15s, border-color 0.15s;

    &:focus {
        outline: none;
        border-color: #9333ea;
        box-shadow: 0 0 0 2px rgba(147, 51, 234, 0.2);
    }

    &::placeholder {
        color: #94a3b8;
    }
`;

const EmailTemplateContentFooter = styled.div`
    display: flex;
    justify-content: flex-end;
    align-items: center;
    padding: 0 4px;
    margin-top: 4px;
    font-size: 12px;
    color: #94a3b8;
    font-style: italic;

    span:last-child {
        font-style: normal;
        text-transform: uppercase;
        font-weight: 700;
        letter-spacing: 0.05em;
    }
`;

const EmailTemplateModalFooter = styled.div`
    padding: 24px 32px;
    background: #f8fafc;
    border-top: 1px solid #f1f5f9;
    display: flex;
    justify-content: flex-end;
    gap: 12px;
    flex-shrink: 0;
`;

const EmailTemplateCancelButton = styled.button`
    padding: 10px 24px;
    border-radius: 0.75rem;
    font-size: 0.875rem;
    font-weight: 700;
    color: #475569;
    background: transparent;
    border: none;
    cursor: pointer;
    transition: background 0.15s;

    &:hover {
        background: #f1f5f9;
        color: #334155;
    }
`;

const EmailTemplateSaveButton = styled.button`
    padding: 10px 32px;
    border-radius: 0.75rem;
    font-size: 0.875rem;
    font-weight: 700;
    color: #fff;
    background: #9333ea;
    border: none;
    cursor: pointer;
    box-shadow: 0 10px 15px -3px rgba(147, 51, 234, 0.2);
    transition: filter 0.15s, transform 0.05s;

    display: inline-flex;  
    align-items: center;    
    justify-content: center;
    gap: 8px; 

    &:hover {
        filter: brightness(1.1);
    }
    &:active {
        transform: scale(0.98);
    }
    &:disabled {
        opacity: 0.6;
        cursor: not-allowed;
    }
`;

const LibraryLabel = styled.label`
  font-size: 12px;
  font-weight: 600;
  color: #374151;
  text-align: left;
  text-transform: uppercase;
`;

const LibrarySearchWrap = styled.div`
  padding: 16px 24px;
  background: rgba(255,255,255,0.6);
  border-bottom: 1px solid #e5e7eb;

  display: flex;
  flex-direction: column;
  gap: 16px;

  @media (min-width: 768px) {
    flex-direction: row;
    align-items: flex-end;
    gap: 20px;
  }
`;

const LibraryFieldGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  align-items: flex-start;
  width: 100%;

  @media (min-width: 768px) {
    width: auto;
    min-width: 0;
    &:first-of-type {
      flex: 1;
    }
  }
`;
const LibrarySearchIcon = styled.span`
  position: absolute;
  left: 12px;
  top: 0;
  bottom: 0;
  width: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #9ca3af;
  pointer-events: none;
`;

const SearchInputWrapper = styled.div`
  position: relative;
  width: 100%;
  min-width: 0;
  display: flex;
  align-items: center;
  min-height: 42px;
`;

const LibraryControls = styled.div`
  display: flex;
  gap: 8px;
`;

const LibrarySearchInput = styled.input`
  width: 100%;
  height: 42px;
  align-items: center;
  margin-bottom: 0;
  

  padding-left: 36px;
  padding-right: 14px;

  font-size: 14px;
  border-radius: 12px;

  border: 1px solid #e5e7eb;
  background: white;

  &:focus {
    outline: none;
    border-color: #9333ea;
    box-shadow: 0 0 0 2px rgba(147, 51, 234, 0.2);
  }
`;


const LibrarySortSelect = styled.select`
  padding: 10px 14px;
  border-radius: 12px;
  margin-bottom: 0;

  font-size: 14px;
  font-weight: 500;

  border: 1px solid #e5e7eb;
  background: white;

  cursor: pointer;

  &:hover {
    background: #f8fafc;
  }

  &:focus {
    outline: none;
    border-color: #9333ea;
    box-shadow: 0 0 0 2px rgba(147, 51, 234, 0.2);
  }
`;

const TemplateLibraryList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-top: 16px;
  padding: 0 20px;

  & > *:last-child {
    margin-bottom: 16px;
  }
`;

const LibraryTemplateCard = styled.div`
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 18px 20px;
  transition: all 0.2s ease;
  display: flex;

  &:hover {
    border-color: #9333ea;
    box-shadow: 0 8px 20px rgba(0,0,0,0.05);
  }
`;

const LibraryTemplateContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
`;

const LibraryTemplateHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
`;

const LibraryTemplateTitle = styled.h3`
  font-size: 16px;
  font-weight: 700;
  color: #111827;
  margin: 0;

  ${LibraryTemplateCard}:hover & {
    color: #9333ea;
  }
`;

const LibraryTemplateDescription = styled.p`
  font-size: 13px;
  color: #6b7280;
  margin: 6px 0 12px 0;
  text-align: left;
`;

const LibraryTemplateMeta = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  font-size: 12px;
  color: #6b7280;
  margin-bottom: 14px;
`;

const MetaRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;

  span {
    font-weight: 600;
  }

  i {
    color: #374151;
  }
`;

const LibraryTemplateActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
`;

const LibraryTemplateFooter = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-end; 
  margin-top: 12px;

  span {
    font-size: 12px;
    color: #6b7280;
  }
`;

const ImportButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  font-size: 13px;
  font-weight: 600;
  border-radius: 8px;
  background: #9333ea;
  color: white;
  border: none;
  cursor: pointer;

  &:hover {
    background: #7e22ce;
  }
`;

const LibraryHeader = styled.header`
  display: flex;
  width: 100%;
  justify-content: space-between;
  align-items: center;
  padding-bottom: 20px;
  border-bottom: 1px solid #e5e7eb;
  background: #ffffff;
  /* Override ModalContent's p { margin: 0 0 12px 0 } so subtitle has no bottom margin */
  p { margin: 4px 0 0 0; }
`;

const LibraryHeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const HeaderIconBox = styled.div`
  background: rgba(147, 51, 234, 0.1);
  padding: 8px;
  margin-right: 12px;
  border-radius: 10px;
  color: #9333ea;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const LibraryTitle = styled.h2`
  font-size: 18px;
  font-weight: 700;
  color: #111827;
  margin: 0;
  text-align: left;
`;

const LibrarySubtitle = styled.p`
  font-size: 12px;
  color: #6b7280;
  margin: 4px 0 0 0;
  text-align: left;
`;

const HeaderCloseButton = styled.button`
  border: none;
  background: transparent;
  padding: 8px;
  width: 32px;
  height: 32px;
  align-items: center;
  justify-content: center;
  display: flex;
  border-radius: 10%;
  color: #9ca3af;
  cursor: pointer;

  &:hover {
    background: #f3f4f6;
    color: #374151;
  }
`;

const LibraryFooter = styled.footer`
  padding-top: 14px;
  border-top: 1px solid #e5e7eb;
  background: #ffffff;
  display: flex;
  justify-content: space-between;
  align-items: center;
  p { margin: 4px 0 0 0; }
`;

const FooterText = styled.p`
  font-size: 12px;
  color: #6b7280;
  text-align:left;
`;

const Pagination = styled.div`
  display: flex;
  gap: 6px;
`;

const PageButton = styled.button`
  padding: 4px 12px;
  border-radius: 8px;
  border: 1px solid ${({ $active }) => ($active ? "#9333ea" : "#e5e7eb")};
  background: ${({ $active }) => ($active ? "#9333ea" : "white")};
  color: ${({ $active }) => ($active ? "white" : "#374151")};
  font-size: 13px;
  font-weight: ${({ $active }) => ($active ? "700" : "500")};
  cursor: pointer;

  &:hover {
    background: ${({ $active }) => ($active ? "#7e22ce" : "#f9fafb")};
  }
`;

const NextButton = styled.button`
  padding: 4px 8px;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
  background: white;
  cursor: pointer;
  display: flex;
  align-items: center;

  &:hover {
    background: #f9fafb;
  }
`;

const PrevButton = styled(NextButton)`
`;

const PreviewButton = styled.button`
  display:flex;
  align-items:center;
  gap:6px;
  padding:8px 12px;
  font-size:13px;
  border-radius:8px;
  border:1px solid #e5e7eb;
  background:white;
  cursor:pointer;

  &:hover{
    background:#f9fafb;
  }
`;

const PreviewModal = styled.div`
  background:white;
  border-radius:12px;

  width:min(800px,90vw);
  max-height:90vh;

  display:flex;
  flex-direction:column;

  box-shadow:0 20px 40px rgba(0,0,0,0.25);
`;

const PreviewHeader = styled.div`
  padding:20px;
  border-bottom:1px solid rgb(241, 241, 241);

  display:flex;
  justify-content:space-between;
  align-items:flex-start;

  h3{
    margin:0;
    font-size:18px;
  }
  label{
    font-size:12px;
    text-transform:uppercase;
    color:#6b7280;
    font-weight:600;
    display:block;
    text-align:left;
    margin-bottom:6px;
  }

  p{
    margin:4px 0 0 0;
    font-size:13px;
    color:#6b7280;
  }
`;

const PreviewBody = styled.div`
  padding:24px;
  overflow-y:auto;
  line-height:1.6;
  font-size:14px;
  color:#374151;
  text-align:left;
  white-space:pre-wrap;
`;

const PreviewFooter = styled.div`
  padding:16px 20px;
  border-top:1px solid #e5e7eb;

  display:flex;
  justify-content:flex-end;
`;

const ShareModalContainer = styled.div`
  width: min(520px, 95vw);
  background: #fff;
  border-radius: 12px;
  overflow: hidden;
  border: 1px solid #e5e7eb;
  box-shadow: 0 25px 50px rgba(0,0,0,0.25);

  display: flex;
  flex-direction: column;
`;

const ShareModalHeader = styled.div`
  padding: 24px;
  border-bottom: 1px solid #f1f5f9;
  text-align: left;

  display: flex;
  justify-content: space-between;
  align-items: flex-start;
`;

const ShareModalTitle = styled.h2`
  font-size: 22px;
  font-weight: 700;
  color: #0f172a;
  margin: 0;
`;

const ShareModalSubtitle = styled.p`
  font-size: 14px;
  color: #64748b;
  margin-top: 4px;
`;

const CloseButton = styled.button`
  border: none;
  background: transparent;
  cursor: pointer;
  color: #94a3b8;

  &:hover {
    color: #475569;
  }
`;

const ShareModalBody = styled.div`
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 24px;
  max-height: 70vh;
  overflow-y: auto;
`;

const FieldGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  text-align: left;
`;

const FieldLabel = styled.label`
  font-size: 14px;
  font-weight: 600;
  color: #0f172a;
`;

const ShareModalInput = styled.input`
  width: 100%;
  padding: 10px 14px;
  font-size: 14px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  color: #0f172a;
  &:focus {
    outline: none;
    border-color: #9333ea;
    box-shadow: 0 0 0 2px rgba(147, 51, 234, 0.2);
  }
`;

const ShareModalTextarea = styled.textarea`
  width: 100%;
  min-height: 120px;
  padding: 10px 14px;
  font-size: 14px;
  line-height: 1.5;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  color: #0f172a;
  resize: vertical;
  font-family: inherit;
  &:focus {
    outline: none;
    border-color: #9333ea;
    box-shadow: 0 0 0 2px rgba(147, 51, 234, 0.2);
  }
`;

const Required = styled.span`
  color:rgb(234, 51, 51);
`;

const DescriptionTextarea = styled.textarea`
  width: 100%;
  min-height: 140px;

  border-radius: 10px;
  border: 1px solid #e2e8f0;

  padding: 14px;
  font-size: 14px;
  line-height: 1.6;

  resize: none;

  &:focus {
    outline: none;
    border-color: #9333ea;
    box-shadow: 0 0 0 2px rgba(147,51,234,0.15);
  }
`;

const InfoBox = styled.div`
  display: flex;
  gap: 12px;
  padding: 16px;

  border-radius: 10px;

  background: rgba(147,51,234,0.06);
  border: 1px solid rgba(147,51,234,0.15);

  color: #475569;
`;

const InfoText = styled.p`
  font-size: 13px;
  line-height: 1.4;
`;

const ShareModalFooter = styled.div`
  padding: 20px 24px;
  background: #f8fafc;

  border-top: 1px solid #f1f5f9;

  display: flex;
  justify-content: flex-end;
  gap: 12px;
`;

const CancelButton = styled.button`
  padding: 10px 20px;
  border-radius: 8px;

  border: 1px solid #e2e8f0;
  background: transparent;

  font-weight: 600;
  cursor: pointer;

  &:hover {
    background: #f1f5f9;
  }
`;

const ShareButton = styled.button`
  padding: 10px 22px;
  border-radius: 8px;

  background: #9333ea;
  color: white;

  font-weight: 700;
  border: none;

  cursor: pointer;

  display: inline-flex;  
  align-items: center;   
  justify-content: center;
  gap: 8px;

  box-shadow: 0 6px 14px rgba(147,51,234,0.25);

  &:hover {
    filter: brightness(1.1);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const Spinner = styled.div`
  width: 14px;
  height: 14px;
  border: 2px solid rgba(255,255,255,0.4);
  border-top-color: white;
  border-radius: 50%;
  animation: spin 0.6s linear infinite;
  flex-shrink: 0;

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

export default CourseSettings;
